import React, { useState } from 'react';
import { Typography, Divider, useMediaQuery, useTheme } from '@mui/material';
import { SearchFilterSortBar } from 'components/pages/certificates/productPage/SearchFilterSortBar';
import { Provenance } from 'components/pages/certificates/productPage/Provenance';
import { ProvenanceFilter } from 'components/pages/certificates/productPage/ProvenanceFilter';
import { ProvenanceSortDrawer } from 'components/pages/certificates/productPage/ProvenanceSortDrawer';
import SortMenuDesktop from 'components/pages/certificates/SortMenuDesktop';
import SortConfigPopover from 'components/pages/certificates/SortConfigPopover';
import { OwnerProvenanceCard } from 'components/pages/certificates/productPage/OwnerProvenanceCard';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { CertificateCustomer } from 'types';
import { getOwner } from 'utils/parsers';
import { PAGE_HEADER_STICKY_HEIGHT } from 'components/UI/PageHeader';

/**
 * VERA-315 — SORT IS A MENU ON DESKTOP, A BOTTOM SHEET ON A PHONE.
 *
 * Both halves were the sheet, so at 1280 clicking Sort slid a phone drawer up from
 * the bottom of the window. This mirrors the branch the certificates list and the
 * Activity tab already carry, with this screen's own fields: at md+ the Sort button
 * anchors `SortMenuDesktop` (the field picker, before anything is sorted) or
 * `SortConfigPopover` (once a sort exists).
 *
 * Keys must match `ProvenanceSortField`; labels match what `SortField` resolves
 * those keys to, so the picker and the configure popover say the same word.
 */
const PROVENANCE_SORT_FIELDS = ['owner_name', 'date', 'sell_price'];

const PROVENANCE_SORT_MENU_FIELDS = [
  { label: 'Owner name', value: 'owner_name' },
  { label: 'Date', value: 'date' },
  { label: 'Sell price', value: 'sell_price' },
];

interface ProvenanceTabContentProps {
  isInPreviewMode?: boolean;
  isShowProvenance: boolean;
  /**
   * VERA-299: gates the CURRENT owner block below. This block previously sat OUTSIDE
   * the `isShowProvenance` gate, so a viewer allowed to see provenance saw the current
   * owner's name and email even when "Show your ownership" was off — the toggle said
   * one thing and the provenance surface did another.
   */
  isShowOwnership?: boolean;
  certificateId: string;
  certificate: CertificateCustomer;
}

export const ProvenanceTabContent: React.FC<ProvenanceTabContentProps> = ({
  isInPreviewMode,
  isShowProvenance,
  isShowOwnership = true,
  certificateId,
  certificate,
}) => {
  const [provenanceQuery, setProvenanceQuery] = useState<string>('');
  const [provenanceFilterOpen, setProvenanceFilterOpen] = useState<boolean>(false);
  const [provenanceOwnerIds, setProvenanceOwnerIds] = useState<string[]>([]);
  const [provenanceDateFrom, setProvenanceDateFrom] = useState<string>('');
  const [provenanceSortOpen, setProvenanceSortOpen] = useState<boolean>(false);
  const [provenanceSortStep, setProvenanceSortStep] = useState<'pick' | 'configure'>('pick');
  /** Desktop only: the Sort button, as the anchor for whichever popover opens. */
  const [provenanceSortMenuAnchor, setProvenanceSortMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [provenanceSortConfigAnchor, setProvenanceSortConfigAnchor] = useState<HTMLElement | null>(
    null
  );
  const [provenanceSortField, setProvenanceSortField] = useState<
    'owner_name' | 'date' | 'sell_price' | null
  >(null);
  const [provenanceSortDirection, setProvenanceSortDirection] = useState<'ASC' | 'DESC' | null>(
    null
  );
  const [provenanceAdditionalSorts, setProvenanceAdditionalSorts] = useState<
    Array<{ field: 'owner_name' | 'date' | 'sell_price'; direction: 'ASC' | 'DESC' }>
  >([]);

  const theme = useTheme();
  /*
    `useMediaQuery` is evaluated at render, and in this app it does not re-evaluate on
    a live resize — measure on a fresh load at the target width rather than by
    dragging the window.
  */
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  const profileId = useSelector((state: RootState) => state.auth.profile?.id);

  // VERA-299/VERA-304: derived from the `certificate` prop already passed in — no
  // separate fetch (e.g. `useGetCertificatePublic`) here, matching the pattern
  // ProductTabContent uses for its own (mutually-exclusive) "Current owner" block.
  const ownerData = getOwner(
    certificate?.customer,
    certificate?.company,
    certificate?.transfer?.type,
    // VERA-304: while a transfer is still pending the payload carries no `customer`,
    // so the sender of that transfer is who currently owns it. See `getOwner`.
    certificate?.transfer?.fromCustomer
  );

  return (
    <>
      {isShowOwnership && (
        <>
          <Typography
            className='mb-3'
            sx={{ fontSize: 16, fontWeight: 500, color: 'text.primary' }}
          >
            Current owner
          </Typography>
          <OwnerProvenanceCard
            name={ownerData?.title}
            subtext={ownerData?.subtext}
            avatarUrl={ownerData?.image}
            /* The holder's KIND, so a company's brand mark is drawn as a logo and not as
               a face. `getOwner` has always returned this; this call site simply never
               forwarded it, so `ownerType` fell to its 'customer' default and every
               company-held certificate rendered its wordmark in a 9999px circle under
               `object-fit: cover`. Measured on the DAC mark (98x59, aspect 1.661) in a
               40px circle: cover scales it to 66.4px wide and crops 13.2px off EACH
               side -- 27% of the signature lost from both ends. That is the exact
               failure `EntityAvatar`'s docblock names ("slices the ends off a
               wordmark"), and it was invisible because a symmetric crop reads as a
               mark that is merely badly centred. */
            ownerType={ownerData?.type}
            sinceDate={certificate?.updated_at || certificate?.created_at || ''}
            completionMethod={certificate?.transfer?.completion_method ?? null}
          />

          <Divider className='my-4 border-[#f0f0f0]' />
        </>
      )}

      {/* VERA-315 Phase 4: the "Provenance" section heading that stood here is
          gone. This content used to live in a TAB, where nothing above it said
          what you were looking at; it is now its own screen whose page title is
          "Provenance", and a heading repeating the title 40px below it is the
          same word twice. "Current owner" above stays — it labels a distinct
          block within this screen, which the title does not. */}
      {/* Pins BELOW the page header, so header + toolbar read as one pinned
          block with the hairline at its bottom — the certificates page draws
          exactly that, from a single HeaderBar element. */}
      <SearchFilterSortBar
        subject='provenance'
        stickyTop={PAGE_HEADER_STICKY_HEIGHT}
        query={provenanceQuery}
        onQueryChange={v => setProvenanceQuery(v)}
        filterBadgeCount={provenanceOwnerIds.length + (provenanceDateFrom ? 1 : 0)}
        onOpenFilter={() => setProvenanceFilterOpen(true)}
        sortBadgeCount={
          provenanceSortField !== null && provenanceSortDirection !== null
            ? 1 + provenanceAdditionalSorts.length
            : 0
        }
        onOpenSort={e => {
          const hasSort = provenanceSortField !== null && provenanceSortDirection !== null;
          if (isDesktop) {
            // `e.currentTarget` is the Sort button itself, read synchronously —
            // the same anchor the certificates list hands its two popovers.
            const anchor = (e?.currentTarget as HTMLElement) ?? null;
            if (hasSort) setProvenanceSortConfigAnchor(anchor);
            else setProvenanceSortMenuAnchor(anchor);
            return;
          }
          setProvenanceSortStep(hasSort ? 'configure' : 'pick');
          setProvenanceSortOpen(true);
        }}
        showSortClear={!!(provenanceSortField !== null && provenanceSortDirection !== null)}
        onClearSort={() => {
          setProvenanceSortField(null);
          setProvenanceSortDirection(null);
          setProvenanceAdditionalSorts([]);
        }}
      />

      {isShowProvenance && (
        <Provenance
          profileId={profileId}
          isInPreviewMode={isInPreviewMode}
          certificate={certificate}
          certificateId={certificateId}
          query={provenanceQuery}
          ownerIds={provenanceOwnerIds}
          dateFrom={provenanceDateFrom}
          sortField={provenanceSortField}
          sortDirection={provenanceSortDirection}
          additionalSorts={provenanceAdditionalSorts}
        />
      )}

      <ProvenanceFilter
        open={provenanceFilterOpen}
        onClose={() => setProvenanceFilterOpen(false)}
        certificateId={certificateId}
        initialOwnerIds={provenanceOwnerIds}
        initialDateFrom={provenanceDateFrom}
        /* No `setProvenanceFilterOpen(false)` — the drawer auto-applies, so this
           fires 400ms after any field changes and closing here would dismiss the
           sheet on the user's first tap. Dismissal is `onClose`'s job. */
        onApply={({ ownerIds, dateFrom }) => {
          setProvenanceOwnerIds(ownerIds || []);
          setProvenanceDateFrom(dateFrom || '');
        }}
      />

      {/* PHONE ONLY. Gated rather than merely left closed, matching how the
          certificates list and the Activity tab mount theirs, so the desktop tree
          carries no drawer that a stray state flip could open. */}
      {!isDesktop && (
      <ProvenanceSortDrawer
        open={provenanceSortOpen}
        onClose={() => {
          setProvenanceSortOpen(false);
          setProvenanceSortStep('pick');
        }}
        step={provenanceSortStep}
        setStep={setProvenanceSortStep}
        primaryField={provenanceSortField || 'date'}
        primaryDirection={provenanceSortDirection || 'ASC'}
        additionalSorts={provenanceAdditionalSorts}
        onPrimaryFieldChange={field => {
          setProvenanceSortField(field);
          if (!provenanceSortDirection) {
            setProvenanceSortDirection('ASC');
          }
        }}
        onPrimaryDirectionChange={setProvenanceSortDirection}
        onClearAll={() => {
          setProvenanceSortField(null);
          setProvenanceSortDirection(null);
          setProvenanceAdditionalSorts([]);
        }}
        /* Nothing to do — the sheet auto-applies, so this runs 400ms after any
           change. Closing here would dismiss on the first field pick and rewinding
           the step would yank the user out of `configure` mid-edit; the sort is
           already committed by the handlers above. See `ScansTabContent`. */
        onApply={() => {}}
        /*
          ONE commit callback, fired on Apply. Previously three granular ones
          (change / remove / add) that each wrote straight to page state, so the
          list re-sorted underneath the still-open sheet.
        */
        onAdditionalChange={next => setProvenanceAdditionalSorts(next)}
      />
      )}

      {/* DESKTOP: the field picker, `@repo/ui`'s `MenuButton` in controlled mode —
          literally the component `apps/platform`'s Sort renders, so row height,
          radius, hover and keyboard behaviour cannot drift from it. Opens only when
          nothing is sorted yet; once a sort exists the button opens the configure
          popover below instead. */}
      {isDesktop && (
        <SortMenuDesktop
          anchorEl={provenanceSortMenuAnchor}
          open={Boolean(provenanceSortMenuAnchor)}
          onClose={() => setProvenanceSortMenuAnchor(null)}
          fields={PROVENANCE_SORT_MENU_FIELDS}
          onPickField={(field, anchor) => {
            setProvenanceSortField(field as 'owner_name' | 'date' | 'sell_price');
            if (!provenanceSortDirection) setProvenanceSortDirection('ASC');
            setProvenanceSortMenuAnchor(null);
            // Hand off to the configure popover — see `SortMenuDesktop`'s
            // "The pick HANDS OFF".
            setProvenanceSortConfigAnchor(anchor);
          }}
        />
      )}

      {isDesktop && (
        <SortConfigPopover
          anchorEl={provenanceSortConfigAnchor}
          open={Boolean(provenanceSortConfigAnchor)}
          onClose={() => setProvenanceSortConfigAnchor(null)}
          availableFields={PROVENANCE_SORT_FIELDS}
          primaryField={provenanceSortField || 'date'}
          primaryDirection={provenanceSortDirection || 'ASC'}
          additionalSorts={provenanceAdditionalSorts}
          disabledFields={[
            ...(provenanceSortField ? [provenanceSortField] : []),
            ...provenanceAdditionalSorts.map(sort => sort.field),
          ]}
          onPrimaryFieldChange={field =>
            setProvenanceSortField(field as 'owner_name' | 'date' | 'sell_price')
          }
          onPrimaryDirectionChange={direction => setProvenanceSortDirection(direction)}
          onClearAll={() => {
            setProvenanceSortField(null);
            setProvenanceSortDirection(null);
            setProvenanceAdditionalSorts([]);
          }}
          onChangeAdditional={(index, next) =>
            setProvenanceAdditionalSorts(current =>
              current.map((sort, i) =>
                i === index
                  ? (next as { field: 'owner_name' | 'date' | 'sell_price'; direction: 'ASC' | 'DESC' })
                  : sort
              )
            )
          }
          onRemoveAdditional={index =>
            setProvenanceAdditionalSorts(current => current.filter((_, i) => i !== index))
          }
          onAddAdditional={field =>
            setProvenanceAdditionalSorts(current => [
              ...current,
              { field: field as 'owner_name' | 'date' | 'sell_price', direction: 'DESC' },
            ])
          }
        />
      )}
    </>
  );
};
